import myTraderaItemActions from '../state/actions/my-tradera-items';
import _ from 'underscore';
import featureToggles from '../utils/feature-toggles';
import { getMessagingThreadLink } from './messaging-helpers';
import myTraderaActions from '../state/actions/my-tradera';
import { types } from './item-actions-helper';

const getId = item => item.key;
const getIds = item => [item.itemId];

// isPrimaryAction = will be primary action unless one before has not already been set as primary action
// Hence, the order of the actions matter.
const commonActions = item => ({
    sellSimilar: () => ({
        id: getId(item),
        type: types.LINK,
        label: 'Sälj liknande',
        getTranslatedLabel: t => t('myt_itemCard_actions_sellSimilar'),
        url: `/selling/sell-similar/${item.itemId}`,
        gaLabel: 'SellSimilar',
        isVisible: () => true,
        isPrimaryAction: () => true
    })
});

export const soldActions = (
    item,
    listViewType,
    memberId,
    memberMessagingParticipation
) => {
    const common = commonActions(item);
    const actions = [
        () => ({
            id: getId(item),
            type: types.LINK,
            label: 'Orderinformation',
            getTranslatedLabel: t => t('myt_itemCard_actions_orderInformation'),
            url: `/my/sold/buyer-info?purchaseOrderId=${item.transaction.purchaseOrderId}`,
            gaLabel: 'BuyerInfo',
            isVisible: () => !!item.transaction.purchaseOrderId,
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Kontakta köpare',
            getTranslatedLabel: t => t('myt_itemCard_actions_contactBuyer'),
            dispatch: dispatch => {
                const recipientId = item.seller.id;
                const src = getMessagingThreadLink(
                    recipientId,
                    item.transaction.buyer.id,
                    item.itemId,
                    {
                        traderaUserId: recipientId,
                        singleThread: true,
                        openForm: true,
                        source: 'contact_buyer'
                    }
                );
                const title = 'Kontakta köpare';
                dispatch(
                    myTraderaActions.openMessagingModal({
                        title,
                        src,
                        recipientId,
                        item
                    })
                );
            },
            gaLabel: 'ContactBuyer',
            isVisible: () => {
                return (
                    featureToggles.isMessagingEnabled &&
                    featureToggles.isMessagingBuyerButtonsEnabled &&
                    memberMessagingParticipation &&
                    item.transaction.purchaseOrderId &&
                    item.transaction.buyer
                );
            },
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Markera som betald',
            getTranslatedLabel: t => t('myt_itemCard_actions_markAsPaid'),
            dispatch: dispatch =>
                dispatch(
                    myTraderaItemActions.markPurchaseOrdersAsPaidBySeller(
                        [item.transaction.purchaseOrderId],
                        listViewType,
                        memberMessagingParticipation
                    )
                ),
            gaLabel: 'MarkAsPaid',
            isVisible: () => {
                if (item.transaction.isPaidWithIntegratedPaymentOption) {
                    return false;
                }
                return !item.transaction.sellerState.paid;
            },
            isPrimaryAction: () => true
        }),
        () => ({
            id: getId(item),
            type: types.LINK,
            label: 'Boka frakt',
            getTranslatedLabel: t => t('myt_itemCard_actions_bookShipping'),
            url: `/my/shipping?purchaseOrderId=${item.transaction.purchaseOrderId}&originUrl=%2fmy%2fsold`,
            /*
             * @todo
             */
            gaLabel: 'BookShipping',
            isVisible: () => {
                if (
                    item.transaction.sellerState.shippingBooked ||
                    item.transaction.sellerState.shipped ||
                    item.transaction.hasUnpaidShipping ||
                    item.isArchived
                ) {
                    return false;
                }
                return true;
            },
            isPrimaryAction: () => {
                const isTakeaway =
                    item.transaction.selectedShipping &&
                    item.transaction.selectedShipping.isTakeaway;
                return (
                    !item.transaction.hasUnpaidShipping &&
                    !isTakeaway &&
                    !item.isArchived
                );
            }
        }),
        () => ({
            id: getId(item),
            type: types.LINK,
            label: 'Betala frakt',
            getTranslatedLabel: t => t('myt_itemCard_actions_payShipping'),
            url: item.transaction.unpaidShippingUrl || `/my/shipping/checkout`,
            gaLabel: 'PayShipping',
            isVisible: () => item.transaction.hasUnpaidShipping,
            isPrimaryAction: () => item.transaction.hasUnpaidShipping
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Avmarkera som bokad',
            getTranslatedLabel: t =>
                t('myt_itemCard_actions_unmarkAsShippingBooked'),
            dispatch: dispatch => {
                dispatch(
                    myTraderaItemActions.sellingUnmarkAsShippingBooked(
                        [item.transaction.purchaseOrderId],
                        listViewType,
                        memberMessagingParticipation
                    )
                );
            },
            gaLabel: 'UnmarkAsShippingBooked',
            isVisible: () =>
                !item.transaction.trackingLink &&
                item.transaction.sellerState.shippingBooked,
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(item),
            type: types.LINK,
            label: 'Spåra paket',
            getTranslatedLabel: t => t('myt_itemCard_actions_trackShipment'),
            url: `/my/sold/buyer-info?purchaseOrderId=${item.transaction.purchaseOrderId}#shipping`,
            gaLabel: 'TrackShipment',
            isVisible: () => !!item.transaction.trackingLink,
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(item),
            type: types.EXTERNAL_LINK,
            label: 'Kvitto för frakt',
            getTranslatedLabel: t => t('myt_itemCard_actions_shippingReceipt'),
            url: `/my/shipping/receipt/${item.transaction.paidShipmentCheckoutId}`,
            gaLabel: 'ShippingReceipt',
            isVisible: () =>
                item.transaction.paidShipmentCheckoutId &&
                item.transaction.selectedShipping &&
                item.transaction.selectedShipping.type === 'SchenkerPrivpak',
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(item),
            type: types.EXTERNAL_LINK,
            label: 'Ladda ner fraktsedel',
            getTranslatedLabel: t => t('myt_itemCard_actions_shippingLabel'),
            url: `/my/shipping/label/${item.transaction.shipmentId}`,
            gaLabel: 'ShippingLabel',
            isVisible: () =>
                item.transaction.shipmentId &&
                item.transaction.selectedShipping &&
                item.transaction.selectedShipping.type === 'SchenkerPrivpak',
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label:
                item.transaction.selectedShipping &&
                item.transaction.selectedShipping.isTakeaway
                    ? 'Avmarkera som hämtad'
                    : 'Avmarkera som skickad',
            getTranslatedLabel: t =>
                item.transaction.selectedShipping &&
                item.transaction.selectedShipping.isTakeaway
                    ? t('myt_itemCard_actions_unmarkAsCollected')
                    : t('myt_itemCard_actions_unmarkAsSent'),
            dispatch: dispatch => {
                dispatch(
                    myTraderaItemActions.sellingUnmarkAsShipped(
                        [item.transaction.transactionId],
                        listViewType
                    )
                );
            },
            gaLabel: 'UnmarkAsShipped',
            isVisible: () => item.transaction.sellerState.shipped,
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label:
                item.transaction.selectedShipping &&
                item.transaction.selectedShipping.isTakeaway
                    ? 'Markera som hämtad'
                    : 'Markera som skickad',
            getTranslatedLabel: t =>
                item.transaction.selectedShipping &&
                item.transaction.selectedShipping.isTakeaway
                    ? t('myt_itemCard_actions_markAsCollected')
                    : t('myt_itemCard_actions_markAsSent'),
            dispatch: dispatch => {
                dispatch(
                    myTraderaItemActions.sellingMarkAsShipped(
                        [item.transaction.transactionId],
                        listViewType
                    )
                );
            },
            gaLabel: 'MarkAsShipped',
            isVisible: () => !item.transaction.sellerState.shipped,
            isPrimaryAction: () => !item.transaction.hasUnpaidShipping
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Lämna omdöme',
            getTranslatedLabel: t => t('myt_itemCard_actions_leaveFeedback'),
            dispatch: dispatch => {
                dispatch(
                    myTraderaItemActions.sellingLeaveFeedback(
                        item,
                        listViewType
                    )
                );
            },
            gaLabel: 'LeaveFeedbackOnBuyer',
            isVisible: () => !item.transaction.sellerState.feedbackLeft,
            isPrimaryAction: () => true
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Återbetala',
            getTranslatedLabel: t => t('myt_itemCard_actions_refund'),
            dispatch: dispatch => dispatch(myTraderaItemActions.refund(item)),
            gaLabel: 'Refund',
            isVisible: () => item.transaction.isDiscountAfterPaymentSupported,
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(item),
            type: types.LINK,
            label: 'Köparens omdömen',
            getTranslatedLabel: t => t('myt_itemCard_actions_buyerFeedback'),
            url: `/profile/feedback/${item.transaction.buyer.id}`,
            gaLabel: 'BuyerProfile',
            isVisible: () => true,
            isPrimaryAction: () => true
        }),
        () => ({
            id: getId(item),
            type: types.LINK,
            label: 'Se köphistorik',
            getTranslatedLabel: t => t('myt_itemCard_actions_purchaseHistory'),
            url: `/my/item/${item.itemId}/purchasehistory`,
            gaLabel: 'PurchaseHistory',
            isVisible: () =>
                !(
                    item.listing.itemType === 'Auction' ||
                    item.listing.itemType === 'AuctionBin'
                ),
            isPrimaryAction: () => true
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Avmarkera som betald',
            getTranslatedLabel: t => t('myt_itemCard_actions_unmarkAsPaid'),
            dispatch: dispatch =>
                dispatch(
                    myTraderaItemActions.unmarkPurchaseOrdersAsPaidBySeller(
                        [item.transaction.purchaseOrderId],
                        listViewType,
                        memberMessagingParticipation
                    )
                ),
            gaLabel: 'UnmarkAsPaid',
            isVisible: () => {
                if (item.transaction.isPaidWithIntegratedPaymentOption) {
                    return false;
                }
                return !!item.transaction.sellerState.paid;
            },
            isPrimaryAction: () => true
        }),
        () => ({
            // "UPI" = "Unpaid Item"
            id: getId(item),
            type: types.LINK,
            label: 'Anmäl utebliven betalning',
            getTranslatedLabel: t => t('myt_itemCard_actions_reportUnpaidItem'),
            url: `/resolution/UPI/${item.transaction.transactionId}`,
            gaLabel: 'ReportUnpaidItem',
            isVisible: () =>
                item.transaction.sellerState.state === 'NotPaid' &&
                !item.transaction.sellerState.upiSubmitted,
            isPrimaryAction: () => true
        }),
        common.sellSimilar,
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Lämna rabatt',
            getTranslatedLabel: t => t('myt_itemCard_actions_giveDiscount'),
            dispatch: dispatch => dispatch(myTraderaItemActions.discount(item)),
            gaLabel: 'Discount',
            isVisible: () => item.transaction.isDiscountBeforePaymentSupported,
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Ångra dölj',
            getTranslatedLabel: t => t('myt_itemCard_actions_unhide'),
            dispatch: dispatch => {
                dispatch(
                    myTraderaItemActions.sellerUnhideTransaction(
                        [item.transaction.purchaseOrderId],
                        listViewType
                    )
                );
            },
            gaLabel: 'UnhideSold',
            isVisible: () =>
                item.transaction && item.transaction.sellerState.hidden,
            isPrimaryAction: () => true
        })
    ];

    const hide = () => ({
        id: getId(item),
        type: types.DISPATCH,
        label: 'Dölj',
        getTranslatedLabel: t => t('myt_itemCard_actions_hide'),
        dispatch: dispatch => {
            dispatch(
                myTraderaItemActions.sellerHideTransaction(
                    [item.transaction.purchaseOrderId],
                    listViewType
                )
            );
        },
        gaLabel: 'HideSold',
        isVisible: () =>
            item.transaction && !item.transaction.sellerState.hidden,
        isPrimaryAction: () => true
    });

    if (item.transaction && item.transaction.sellerState.state === 'Done') {
        // Make "hide" the primary action by setting it as the first element in the array
        actions.unshift(hide);
    } else {
        actions.push(hide);
    }

    return actions;
};

export const activeSellerActions = (
    item,
    listViewType,
    memberId,
    memberMessagingParticipation
) => {
    const isShopItem = item.listing.itemType === 'Shopitem';
    const common = commonActions(item);
    const actions = [
        () => ({
            id: getId(item),
            type: isShopItem ? types.DISABLED : types.LINK,
            tooltip: isShopItem
                ? 'Använd Sello för att ändra butiksannonser'
                : null,
            label: 'Ändra',
            getTranslatedLabel: t => t('myt_itemCard_actions_update'),
            url: `/selling/edit/${item.itemId}`,
            gaLabel: isShopItem
                ? 'EditShopItem'
                : item.bidInformation.bidCount === 0
                ? 'EditItem'
                : 'AddText',
            isVisible: () => true,
            isPrimaryAction: () => true
        }),
        common.sellSimilar,
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Kontakta högstbjudande',
            getTranslatedLabel: t =>
                t('myt_itemCard_actions_contactHighestBidder'),
            dispatch: dispatch => {
                const recipientId = item.seller.id;
                const src = getMessagingThreadLink(
                    recipientId,
                    item.bidInformation.leadingBidder &&
                        item.bidInformation.leadingBidder.id,
                    item.itemId,
                    {
                        traderaUserId: recipientId,
                        singleThread: true,
                        openForm: true,
                        source: 'contact_highest_bidder'
                    }
                );
                const title = 'Kontakta högstbjudande';
                dispatch(
                    myTraderaActions.openMessagingModal({
                        title,
                        src,
                        recipientId,
                        item
                    })
                );
            },
            gaLabel: 'ContactHighestBidder',
            isVisible: () => {
                return (
                    featureToggles.isMessagingEnabled &&
                    featureToggles.isMessagingBuyerButtonsEnabled &&
                    memberMessagingParticipation &&
                    item.bidInformation.bidCount > 0
                );
            },
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Kontakta högstbjudande',
            getTranslatedLabel: t =>
                t('myt_itemCard_actions_contactHighestBidder'),
            dispatch: (dispatch, props) => {
                dispatch(
                    myTraderaItemActions.contactHighestBidder(
                        item,
                        props.sellerEmail
                    )
                );
            },
            gaLabel: 'ContactHighestBidder',
            isVisible: () => {
                return (
                    (!featureToggles.isMessagingEnabled ||
                        !featureToggles.isMessagingBuyerButtonsEnabled ||
                        !memberMessagingParticipation) &&
                    item.bidInformation.bidCount > 0
                );
            },
            isPrimaryAction: () => true
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Avbryt annons',
            getTranslatedLabel: t => t('myt_itemCard_actions_cancelItem'),
            dispatch: dispatch => {
                dispatch(myTraderaItemActions.cancelItem(item));
            },
            gaLabel: 'EndItem',
            isVisible: () => item.bidInformation.bidCount === 0,
            isPrimaryAction: () => true
        })
    ];
    return actions;
};

export const unsoldActions = (item, listViewType, memberId) => {
    const actions = [
        () => ({
            id: getId(item),
            type: types.LINK,
            label: item.listing.restart.hasRestartedChild
                ? 'Sälj liknande'
                : 'Lägg upp igen',
            getTranslatedLabel: t =>
                item.listing.restart.hasRestartedChild
                    ? t('myt_itemCard_actions_sellSimilar')
                    : t('myt_itemCard_actions_restart'),
            url: item.listing.restart.hasRestartedChild
                ? `/selling/sell-similar/${item.itemId}`
                : `/selling/restart/${item.itemId}`,
            gaLabel: 'Restart',
            isVisible: () => true,
            isPrimaryAction: () => true
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Dölj',
            getTranslatedLabel: t => t('myt_itemCard_actions_hide'),
            dispatch: dispatch => {
                dispatch(
                    myTraderaItemActions.sellerHideUnsold(
                        getIds(item),
                        listViewType
                    )
                );
            },
            gaLabel: 'HideUnsold',
            isVisible: () =>
                !item.transaction && !_.contains(item.hiddenByMember, memberId),
            isPrimaryAction: () => true
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Visa',
            getTranslatedLabel: t => t('myt_itemCard_actions_show'),
            dispatch: dispatch => {
                dispatch(
                    myTraderaItemActions.sellerUnhideUnsold(
                        getIds(item),
                        listViewType
                    )
                );
            },
            gaLabel: 'UnhideUnsold',
            isVisible: () =>
                !item.transaction && _.contains(item.hiddenByMember, memberId),
            isPrimaryAction: () => true
        })
    ];
    return actions;
};
